import { CarAxleType, TireClassification, TireQuoteItemRecord, TireQuoteItemRecordCustomer } from '@navio/nsdk';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { formatCurrencyAmount, getDimensionsString, getTireBrandName, getTirePrice } from 'noddi-util';
import { TireEuGradeBadge } from '../Tire/TireEuGradeBadge';
import { getBestTireToDisplay } from './utils';

export type CompactTireSelectionCardTranslations = {
  recommended: string;
  budget: string;
  standard: string;
  premium: string;
};

type CompactTireSelectionCardProps = {
  quoteItem: TireQuoteItemRecord | TireQuoteItemRecordCustomer;
  isSelected: boolean;
  onSelect: () => void;
  /** Extra amount added to the displayed total (e.g. fitting / generic items from the parent offer). */
  additionalAmount?: number;
  translations?: CompactTireSelectionCardTranslations;
  className?: string;
};

const DEFAULT_TRANSLATIONS: CompactTireSelectionCardTranslations = {
  recommended: 'Recommended',
  budget: 'Budget',
  standard: 'Standard',
  premium: 'Premium'
};

export const CompactTireSelectionCard = ({
  quoteItem,
  isSelected,
  onSelect,
  additionalAmount = 0,
  translations = DEFAULT_TRANSLATIONS,
  className
}: CompactTireSelectionCardProps) => {
  const isRecommended = quoteItem.isRecommended;
  const { headlineTire, totalAmount: tireTotal, currency } = summarizeQuoteItem(quoteItem);
  const totalAmount = tireTotal + additionalAmount;

  if (!headlineTire) {
    return null;
  }

  const inventoryTire = headlineTire.tireInventory.tire;
  const tier = getTier(inventoryTire.classification);
  const imageUrl = headlineTire.tireInventory.imageTire?.url;

  return (
    <button
      type='button'
      onClick={onSelect}
      aria-pressed={isSelected}
      className={cn(
        'group flex flex-col gap-2 rounded-lg border-2 p-3 text-left transition-colors duration-150',
        'focus-visible:ring-2 focus-visible:ring-card-selected-background/40 focus-visible:outline-hidden',
        isSelected
          ? 'border-card-selected-background bg-card-light-background'
          : 'border-border bg-card hover:border-card-selected-background/40',
        className
      )}
    >
      <div className='flex items-start gap-2.5'>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={`${getTireBrandName(inventoryTire)} ${inventoryTire.model ?? ''}`}
            className='size-9 shrink-0 rounded object-contain'
          />
        ) : (
          <div className='size-9 shrink-0 rounded bg-muted' />
        )}

        <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
          <div className='flex flex-wrap items-center gap-1'>
            {tier && <TierPill tier={tier} label={translations[tier]} />}
            {isRecommended && <RecommendedBadge label={translations.recommended} />}
          </div>
          <div className='truncate text-xs font-semibold text-primary'>{getTireBrandName(inventoryTire)}</div>
          <div className='truncate text-[11px] text-muted-foreground'>
            {inventoryTire.model ?? getDimensionsString(inventoryTire)}
          </div>
        </div>

        <RadioDot isSelected={isSelected} />
      </div>

      <div className='flex items-center justify-between gap-2 border-t border-border pt-1.5'>
        <div className='flex items-center gap-1'>
          {inventoryTire.wetGripClass && <TireEuGradeBadge grade={inventoryTire.wetGripClass} size='small' />}
          {inventoryTire.rollingResistanceClass && (
            <TireEuGradeBadge grade={inventoryTire.rollingResistanceClass} size='small' />
          )}
          {inventoryTire.soundDb ? (
            <span className='ml-0.5 text-[10px] text-muted-foreground tabular-nums'>{inventoryTire.soundDb}dB</span>
          ) : null}
        </div>
        <div
          className={cn('text-right text-sm font-bold tabular-nums', isSelected ? 'text-primary' : 'text-foreground')}
        >
          {formatCurrencyAmount({ money: { amount: totalAmount, currency }, decimals: 0 })}
        </div>
      </div>
    </button>
  );
};

function summarizeQuoteItem(quoteItem: TireQuoteItemRecord | TireQuoteItemRecordCustomer) {
  const tires = quoteItem.quoteItemTires;
  if (tires.length === 0) {
    return { headlineTire: undefined, totalAmount: 0, currency: 'NOK' as const };
  }
  const totalAmount = tires.reduce((sum, tire) => sum + getTirePrice(tire).amount * (tire.quantityDefault ?? 0), 0);
  const currency = getTirePrice(tires[0]!).currency;

  if (quoteItem.isStaggered) {
    const front = tires.find((tire) => tire.axle?.type === CarAxleType.FRONT);
    const rear = tires.find((tire) => tire.axle?.type === CarAxleType.REAR);
    if (front && rear) {
      const better = getBestTireToDisplay({
        frontTireFromInventorySupplier: front.tireInventory,
        rearTireFromInventorySupplier: rear.tireInventory
      });
      return {
        headlineTire: better === front.tireInventory ? front : rear,
        totalAmount,
        currency
      };
    }
  }
  return { headlineTire: tires[0]!, totalAmount, currency };
}

type TierKey = 'budget' | 'standard' | 'premium';

function getTier(classification: string | null | undefined): TierKey | null {
  switch (classification) {
    case TireClassification.BUDGET:
    case TireClassification.ECONOMY:
      return 'budget';
    case TireClassification.QUALITY:
      return 'standard';
    case TireClassification.PREMIUM:
      return 'premium';
    default:
      return null;
  }
}

const TIER_STYLES: Record<TierKey, string> = {
  budget: 'bg-warning50 text-warningDark',
  standard: 'bg-card-light-background text-primary',
  premium: 'bg-success20 text-successDark'
};

const TierPill = ({ tier, label }: { tier: TierKey; label: string }) => (
  <span
    className={cn(
      'inline-flex items-center rounded-full px-1.5 py-px text-[9px] font-semibold tracking-wider uppercase',
      TIER_STYLES[tier]
    )}
  >
    {label}
  </span>
);

const RecommendedBadge = ({ label }: { label: string }) => (
  <span className='inline-flex items-center gap-0.5 rounded-full border border-card-selected-background/40 bg-card px-1.5 py-px text-[9px] font-bold tracking-wider text-primary uppercase'>
    <NoddiIcon name='Star' size='xSmall' />
    {label}
  </span>
);

const RadioDot = ({ isSelected }: { isSelected: boolean }) => (
  <span
    className={cn(
      'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border-2 transition-colors',
      isSelected ? 'border-card-selected-background bg-card-selected-background' : 'border-border bg-card'
    )}
  >
    {isSelected && <span className='size-1.5 rounded-full bg-card-selected-foreground' />}
  </span>
);
